import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { ApiError } from '../api/client'
import { PageHeader } from '../components/Page'
import { endpoints, type CatalogItem, type Conflict, type Rule, type Staff } from '../api/endpoints'
import { formatRupiah, parsePercentBps, parseRupiah } from '../money'
import { errorMessage, t } from '../strings'

type Tab = 'items' | 'staff' | 'rules'

function formatRate(r: Pick<Rule, 'rule_type' | 'rate'>): string {
  return r.rule_type === 'fixed' ? formatRupiah(r.rate) : `${(r.rate / 100).toString().replace('.', ',')}%`
}

function valueText(field: string, v: unknown): string {
  if (typeof v === 'number' && (field === 'price' || field === 'cost')) return formatRupiah(v)
  if (typeof v === 'boolean') return v ? t.active.toLowerCase() : t.inactive.toLowerCase()
  if (field.startsWith('pin')) return '••••••'
  return v === null || v === undefined ? '-' : String(v)
}

function saveError(e: unknown): string {
  return errorMessage(e instanceof ApiError ? e.code : 'network')
}

/** Catalog, staff, commission rules (P4-08), and notices of phone edits that lost (ADR-0005 cl. 1). */
export function DataPage() {
  const [tab, setTab] = useState<Tab>('items')
  return (
    <div className="narrow">
      <PageHeader title={t.dataTitle} subtitle={t.syncNote} />
      <Conflicts />
      <div className="chips" role="tablist">
        {(
          [
            ['items', t.tabItems],
            ['staff', t.tabStaff],
            ['rules', t.tabRules],
          ] as const
        ).map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'chip on' : 'chip'} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'items' && <Items />}
      {tab === 'staff' && <StaffList />}
      {tab === 'rules' && <Rules />}
    </div>
  )
}

function Conflicts() {
  const qc = useQueryClient()
  const conflicts = useQuery({ queryKey: ['conflicts'], queryFn: endpoints.conflicts })
  const seen = useMutation({
    mutationFn: endpoints.conflictsSeen,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conflicts'] }),
  })
  if (!conflicts.data?.length) return null
  return (
    <section className="card warn-card">
      <h2>{t.conflictsTitle}</h2>
      <ul className="list">
        {conflicts.data.map((c: Conflict) => (
          <li key={c.id} className="small">
            {t.conflictLine(
              t.fields[c.field] ?? c.field,
              c.entity_name ?? '-',
              valueText(c.field, c.device_value),
              valueText(c.field, c.server_value),
            )}
          </li>
        ))}
      </ul>
      <button className="secondary" disabled={seen.isPending} onClick={() => seen.mutate()}>
        {t.markSeen}
      </button>
    </section>
  )
}

// ---------- catalog

function Items() {
  const items = useQuery({ queryKey: ['catalog'], queryFn: endpoints.catalog })
  const [editing, setEditing] = useState<CatalogItem | 'new' | null>(null)
  if (editing) return <ItemForm item={editing === 'new' ? null : editing} done={() => setEditing(null)} />
  return (
    <>
      <button onClick={() => setEditing('new')}>{t.add}</button>
      {items.data?.length === 0 && <p className="muted">{t.noItems}</p>}
      <ul className="list">
        {items.data?.map((i) => (
          <li key={i.id}>
            <button className="card line pick" onClick={() => setEditing(i)}>
              <div>
                <div className="line-title">{i.name}</div>
                <div className="muted small">
                  {i.item_type === 'service' ? t.service : t.product}
                  {i.category && ` · ${i.category}`}
                  {!i.active && ` · ${t.inactive}`}
                </div>
              </div>
              <div className="amount">{formatRupiah(i.price)}</div>
            </button>
          </li>
        ))}
      </ul>
    </>
  )
}

function ItemForm({ item, done }: { item: CatalogItem | null; done: () => void }) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (body: Parameters<typeof endpoints.saveItem>[1]) => endpoints.saveItem(item?.id ?? crypto.randomUUID(), body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['catalog'] })
      done()
    },
    onError: (e) => setError(saveError(e)),
  })
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    const price = parseRupiah(String(f.get('price') ?? ''))
    if (price === null) return setError(errorMessage('invalid_request'))
    save.mutate({
      name: String(f.get('name') ?? '').trim(),
      item_type: f.get('item_type') === 'product' ? 'product' : 'service',
      category: String(f.get('category') ?? '').trim(),
      price,
      cost: parseRupiah(String(f.get('cost') ?? '')),
      active: f.get('active') === 'on',
      expected_version: item?.version ?? 0,
    })
  }
  return (
    <form className="card" onSubmit={submit}>
      <label>
        {t.name}
        <input name="name" required maxLength={100} defaultValue={item?.name} />
      </label>
      <label>
        {t.type}
        <select name="item_type" defaultValue={item?.item_type ?? 'service'}>
          <option value="service">{t.service}</option>
          <option value="product">{t.product}</option>
        </select>
      </label>
      <label>
        {t.category}
        <input name="category" maxLength={100} defaultValue={item?.category} />
      </label>
      <label>
        {t.price}
        <input name="price" required inputMode="numeric" defaultValue={item ? formatRupiah(item.price, false) : ''} />
      </label>
      <label>
        {t.cost}
        <input name="cost" inputMode="numeric" defaultValue={item?.cost != null ? formatRupiah(item.cost, false) : ''} />
      </label>
      <label className="check">
        <input type="checkbox" name="active" defaultChecked={item?.active ?? true} /> {t.active}
      </label>
      {error && <p className="error">{error}</p>}
      <div className="grid2">
        <button type="button" className="secondary" onClick={done}>
          {t.cancel}
        </button>
        <button type="submit" disabled={save.isPending}>
          {t.save}
        </button>
      </div>
    </form>
  )
}

// ---------- staff

function StaffList() {
  const staff = useQuery({ queryKey: ['staff'], queryFn: endpoints.staff })
  const [editing, setEditing] = useState<Staff | 'new' | null>(null)
  if (editing) return <StaffForm staff={editing === 'new' ? null : editing} done={() => setEditing(null)} />
  return (
    <>
      <button onClick={() => setEditing('new')}>{t.add}</button>
      {staff.data?.length === 0 && <p className="muted">{t.noStaffYet}</p>}
      <ul className="list">
        {staff.data?.map((s) => (
          <li key={s.id}>
            <button className="card line pick" onClick={() => setEditing(s)}>
              <div className="line-title">{s.name}</div>
              <span className={`badge ${s.active ? 'active' : 'revoked'}`}>{s.active ? t.active : t.inactive}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  )
}

function StaffForm({ staff, done }: { staff: Staff | null; done: () => void }) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (body: Parameters<typeof endpoints.saveStaff>[1]) => endpoints.saveStaff(staff?.id ?? crypto.randomUUID(), body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['staff'] })
      done()
    },
    onError: (e) => setError(saveError(e)),
  })
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    save.mutate({
      name: String(f.get('name') ?? '').trim(),
      active: f.get('active') === 'on',
      expected_version: staff?.version ?? 0,
    })
  }
  return (
    <form className="card" onSubmit={submit}>
      <label>
        {t.name}
        <input name="name" required maxLength={60} defaultValue={staff?.name} />
      </label>
      <label className="check">
        <input type="checkbox" name="active" defaultChecked={staff?.active ?? true} /> {t.active}
      </label>
      {error && <p className="error">{error}</p>}
      <div className="grid2">
        <button type="button" className="secondary" onClick={done}>
          {t.cancel}
        </button>
        <button type="submit" disabled={save.isPending}>
          {t.save}
        </button>
      </div>
    </form>
  )
}

// ---------- commission rules (ADR-0009)

function Rules() {
  const rules = useQuery({ queryKey: ['rules'], queryFn: endpoints.rules })
  const items = useQuery({ queryKey: ['catalog'], queryFn: endpoints.catalog })
  const staff = useQuery({ queryKey: ['staff'], queryFn: endpoints.staff })
  const [editing, setEditing] = useState<Rule | 'new' | null>(null)
  const itemName = new Map(items.data?.map((i) => [i.id, i.name]))
  const staffName = new Map(staff.data?.map((s) => [s.id, s.name]))
  if (editing)
    return (
      <RuleForm rule={editing === 'new' ? null : editing} items={items.data ?? []} staff={staff.data ?? []} done={() => setEditing(null)} />
    )
  return (
    <>
      <button onClick={() => setEditing('new')}>{t.add}</button>
      {rules.data?.length === 0 && <p className="muted">{t.noRules}</p>}
      <ul className="list">
        {rules.data?.map((r) => (
          <li key={r.id}>
            <button className="card line pick" onClick={() => setEditing(r)}>
              <div>
                <div className="line-title">{itemName.get(r.catalog_item_id) ?? '-'}</div>
                <div className="muted small">
                  {r.staff_id ? (staffName.get(r.staff_id) ?? '-') : t.ruleAllStaff}
                  {!r.active && ` · ${t.inactive}`}
                </div>
              </div>
              <div className="amount">{formatRate(r)}</div>
            </button>
          </li>
        ))}
      </ul>
    </>
  )
}

function RuleForm({ rule, items, staff, done }: { rule: Rule | null; items: CatalogItem[]; staff: Staff[]; done: () => void }) {
  const qc = useQueryClient()
  const [type, setType] = useState<Rule['rule_type']>(rule?.rule_type ?? 'percent')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (body: Parameters<typeof endpoints.saveRule>[1]) => endpoints.saveRule(rule?.id ?? crypto.randomUUID(), body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['rules'] })
      done()
    },
    onError: (e) => setError(saveError(e)),
  })
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    const rateText = String(f.get('rate') ?? '')
    const rate = type === 'percent' ? parsePercentBps(rateText) : parseRupiah(rateText)
    if (rate === null) return setError(errorMessage(type === 'percent' ? 'rate_above_100_percent' : 'invalid_request'))
    const staffId = String(f.get('staff_id') ?? '')
    save.mutate({
      catalog_item_id: rule?.catalog_item_id ?? String(f.get('catalog_item_id') ?? ''),
      staff_id: rule ? rule.staff_id : staffId === '' ? null : staffId,
      rule_type: type,
      rate,
      active: f.get('active') === 'on',
      expected_version: rule?.version ?? 0,
    })
  }
  const rateDefault = rule ? (rule.rule_type === 'fixed' ? formatRupiah(rule.rate, false) : String(rule.rate / 100).replace('.', ',')) : ''
  return (
    <form className="card" onSubmit={submit}>
      <label>
        {t.ruleFor}
        <select name="catalog_item_id" defaultValue={rule?.catalog_item_id} disabled={rule !== null} required>
          {items
            .filter((i) => i.active || i.id === rule?.catalog_item_id)
            .map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
        </select>
      </label>
      <label>
        {t.ruleStaff}
        <select name="staff_id" defaultValue={rule?.staff_id ?? ''} disabled={rule !== null}>
          <option value="">{t.ruleAllStaff}</option>
          {staff
            .filter((s) => s.active || s.id === rule?.staff_id)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>
      </label>
      <label>
        {t.ruleType}
        <select value={type} onChange={(e) => setType(e.target.value === 'fixed' ? 'fixed' : 'percent')}>
          <option value="percent">{t.rulePercent}</option>
          <option value="fixed">{t.ruleFixed}</option>
        </select>
      </label>
      <label>
        {type === 'percent' ? t.ruleRatePercent : t.ruleRate}
        <input name="rate" required inputMode="decimal" defaultValue={rateDefault} />
      </label>
      <label className="check">
        <input type="checkbox" name="active" defaultChecked={rule?.active ?? true} /> {t.active}
      </label>
      {error && <p className="error">{error}</p>}
      <div className="grid2">
        <button type="button" className="secondary" onClick={done}>
          {t.cancel}
        </button>
        <button type="submit" disabled={save.isPending}>
          {t.save}
        </button>
      </div>
    </form>
  )
}
